import { computed, Ref, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStekManager } from "@/composables/stek-manager";
import { storeToRefs } from "pinia";
import { api, types } from "@/lib/openapi";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { Artefact } from "@/lib/artefacts";
import { useOrganizationManager } from "@/composables/organization-manager";
import { defineEntityStore } from "@/stores/helpers";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useEntityNameManager } from "@/composables/entity-name-manager";
import {
    discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    useDraftManager,
} from "@/composables/draft-manager";
import { useSyncStore } from "@/stores/sync-store";
import { syncChannel } from "@/lib/broadcast-channels";
import { throttle } from "lodash";
import { useEditManager } from "@/composables/edit-manager";
import { followLink } from "@/lib/openapi/helpers";

export type BusinessEntity = types.BusinessEntity;

// Uses the current route to determine what the 'businessEntity' should be and fetches
// the businessEntity description for you.
export const useBusinessEntityStore = defineEntityStore(
    "businessEntity",
    () => {
        const { organization } = useOrganizationManager();
        const { stek } = useStekManager();

        const businessEntity = ref<BusinessEntity | undefined>(undefined);
        const router = useRouter();
        const syncStore = useSyncStore();
        const entityId = computed(() => businessEntity.value?.identity);
        const description: Ref<Artefact<"text/markdown"> | undefined> = ref();
        const definition: Ref<Artefact<"application/json"> | undefined> = ref();
        const descriptionDraft = useDraftManager({
            entityId,
            entityType: EntityType.BUSINESS_ENTITY,
            attribute: "description",
            artefact: description,
        });
        discardDraftsAfterSync(
            entityId,
            [descriptionDraft],
            EntityType.BUSINESS_ENTITY
        );
        const definitionDraft = useDraftManager({
            entityId,
            entityType: EntityType.BUSINESS_ENTITY,
            attribute: "definition",
            artefact: definition,
        });
        discardDraftsAfterSync(
            entityId,
            [definitionDraft],
            EntityType.BUSINESS_ENTITY
        );

        const { businessEntity: businessEntityInfo } =
            useCurrentEntityManager();

        const isFetchingBusinessEntity = ref(false);
        const isFetchingDescription = ref(false);
        const isFetchingDefinition = ref(false);
        const controller = ref(new AbortController());
        const {
            isModified: isNameModified,
            reset: resetName,
            persist: persistName,
        } = useEntityNameManager(businessEntity);

        async function updatebusinessEntity(force = false) {
            const businessEntityId = businessEntityInfo.value?.identity;

            // In case the same businessEntity is selected, return early.
            if (
                !force &&
                businessEntity.value &&
                businessEntity.value.identity === businessEntityId
            ) {
                return;
            }

            // Abort any previously running requests and create a new controller
            controller.value.abort();
            controller.value = new AbortController();

            if (!businessEntityId) {
                description.value = undefined;
                definition.value = undefined;
                businessEntity.value = undefined;
                return;
            }

            isFetchingBusinessEntity.value = true;
            description.value = undefined;
            definition.value = undefined;
            businessEntity.value = undefined;

            try {
                const { data, response } =
                    await api.startDomainBusinessEntityMgtGetBusinessEntityStartPost(
                        {
                            body: {
                                business_entity_id: `${businessEntityId}`,
                            },
                            signal: controller.value.signal,
                        }
                    );
                if (!response.ok) {
                    if (response.status === 404) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "businessEntity",
                                id: businessEntityId,
                            },
                        });
                    }
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                businessEntity.value = data;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingBusinessEntity.value = false;
            }
            // Fetch description and definition while we're still in the try block
            if (businessEntity.value) {
                await Promise.all([
                    updateDescription(businessEntityId),
                    updateDefinition(businessEntityId),
                ]);
            }
        }
        watch(
            () => businessEntityInfo.value?.identity,
            () => updatebusinessEntity(),
            { immediate: true }
        );

        const updateDescription = async (
            currentBusinessEntityId?: string | undefined
        ) => {
            if (businessEntity.value) {
                try {
                    isFetchingDescription.value = true;
                    const descriptionResult = await getDescriptionArtefact(
                        businessEntity.value,
                        "businessEntityDescription",
                        controller.value.signal
                    );

                    // Check if businessEntity has changed during the async operation
                    if (
                        !businessEntity.value ||
                        businessEntity.value.identity !==
                            currentBusinessEntityId
                    ) {
                        console.debug(
                            "Business Entity changed during description fetch, aborting description update"
                        );
                        return;
                    }

                    description.value = descriptionResult;
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Request was aborted by the AbortController.
                        return;
                    } else {
                        throw e;
                    }
                } finally {
                    isFetchingDescription.value = false;
                }
            }
        };

        const updateDefinition = async (
            currentBusinessEntityId?: string | undefined
        ) => {
            if (businessEntity.value) {
                if (businessEntity.value.definition) {
                    try {
                        isFetchingDefinition.value = true;
                        const { response, data } = await followLink<File>(
                            businessEntity.value.definition,
                            "get",
                            undefined,
                            controller.value.signal
                        );

                        if (!response.ok) {
                            if (response.status === 404) {
                                return;
                            }
                            throw new Error(
                                `HTTP error! status: ${response.status}`
                            );
                        }

                        // Check if businessEntity has changed during the async operation
                        if (
                            !businessEntity.value ||
                            businessEntity.value.identity !==
                                currentBusinessEntityId
                        ) {
                            console.debug(
                                "Business Entity changed during definition fetch, aborting definition update"
                            );
                            return;
                        }

                        const content = JSON.stringify(data);

                        // Check again if businessEntity has changed during content processing
                        if (
                            !businessEntity.value ||
                            businessEntity.value.identity !==
                                currentBusinessEntityId
                        ) {
                            console.debug(
                                "Business Entity changed during definition processing, aborting definition update"
                            );
                            return;
                        }

                        definition.value = new Artefact({
                            identity: businessEntity.value.definition.identity,
                            name: "businessEntityDefinition",
                            mimetype: "application/json",
                            content: content,
                            blob: new Blob([content]),
                        });

                        // Check one more time before setting links
                        if (
                            !businessEntity.value ||
                            businessEntity.value.identity !==
                                currentBusinessEntityId
                        ) {
                            console.debug(
                                "Business Entity changed before setting links, aborting"
                            );
                            return;
                        }

                        definition.value.links =
                            businessEntity.value.definition.links;
                    } catch (e) {
                        if (
                            e instanceof DOMException &&
                            e.name === "AbortError"
                        ) {
                            // Request was aborted by the AbortController.
                            return;
                        } else {
                            throw e;
                        }
                    } finally {
                        isFetchingDefinition.value = false;
                    }
                } else {
                    // Check if businessEntity has changed before setting an empty artefact
                    if (
                        !businessEntity.value ||
                        businessEntity.value.identity !==
                            currentBusinessEntityId
                    ) {
                        console.debug(
                            "Business Entity changed before creating empty artefact, aborting"
                        );
                        return;
                    }

                    // Create an empty definition artefact
                    definition.value = Artefact.createEmptyArtefact({
                        mimetype: "application/json",
                        identity:
                            businessEntity.value!.identity + "_definition",
                    });
                }
            }
        };

        const isModified = computed(() => {
            if (isFetchingBusinessEntity.value) {
                return false;
            }
            return Boolean(
                isNameModified.value ||
                    description.value?.isModified ||
                    definition.value?.isModified
            );
        });

        const queueSave = async () => {
            if (!isModified.value || !businessEntity.value) {
                return;
            }

            const updateContent: types.StartDomainBusinessEntityMgtUpdateBusinessEntityStartPostData =
                {
                    body: {
                        business_entity_id: businessEntity.value.identity,
                    },
                    url: "/domain/business_entity_mgt/update_business_entity/start",
                };

            if (isNameModified.value) {
                updateContent.body.name = businessEntity.value.name;
            }
            if (description.value?.isModified) {
                updateContent.body.description = description.value.blob;
            }

            if (definition.value?.isModified) {
                updateContent.body.definition = definition.value.blob;
            }

            if (
                updateContent.body.business_entity_id &&
                Object.keys(updateContent.body).length === 1
            ) {
                // Only businessEntity_id is set, no need to update.
                return;
            }

            syncStore.queueSync({
                entityType: EntityType.BUSINESS_ENTITY,
                args: [updateContent],
            });

            if (description.value?.isModified) {
                description.value.persist();
            }
            if (definition.value?.isModified) {
                definition.value.persist();
            }
            if (isNameModified.value) {
                persistName();
            }
        };
        const reset = async () => {
            await description.value?.resetContent();
            await definition.value?.resetContent();
            resetName();
        };

        const { isEditing } = useEditManager();
        const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
        watch([isEditing, isModified], ([_, modified]) => {
            if (modified) {
                throttledSave();
            }
        });

        syncChannel.addEventListener(
            "message",
            ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    identity.organizationId === organization.value?.identity &&
                    identity.entityType === EntityType.BUSINESS_ENTITY &&
                    identity.entityId === businessEntity.value?.identity
                ) {
                    updatebusinessEntity(true);
                }
            }
        );

        return {
            businessEntity,
            description,
            definition,
            isFetching: computed(() =>
                Boolean(
                    isFetchingBusinessEntity.value ||
                        isFetchingDefinition.value ||
                        isFetchingDescription.value
                )
            ),
            isModified,
            reset,
        };
    }
);

export const useBusinessEntityManager = () =>
    storeToRefs(useBusinessEntityStore());
